import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import {
  Reservation,
  ReservationCalendar,
  ReservationEquipmentAvailability,
  ReservationExtraordinaryCalendar,
  ReservationInfo,
} from "../model/reservation";
import { Reminder } from "../model/reminder";
import { ReservationSlot } from "../model/reservation-slot";
import {
  MedicalAppointmentCancel,
  MedicalAppointmentCancelReason,
  MedicalAppointmentConfirmation,
} from "../model/medical-appointment-cancel";
import { ReservationSearchItem } from "../model/reservation-search-item";
import { EquipmentPhysisicianChange } from "../model/equipment-physisician-change";
import { ServiceReservation } from "../model/service-reservation";
import { SlotValidationResponse } from "src/app/model/slot-validation-response";
import { CancelledReservationReasonFilter } from "../model/cancelled-reservation-reason-filter";
import { ReservationQuestionInfo } from "../model/reservation-question";
import { LogViewInfo } from "src/app/model/log-view-info";
import { MedicalAppointmentWaitSearchItem } from "../model/medical-appointment/medical-appointment-wait-search-item";
import { MedicalAppointmentWaitTrace } from "../model/medical-appointment/medical-appointment-wait-trace";
import { MedicalAppointmentWaitState } from "../model/medical-appointment/medical-appointment-wait-state";
import { ReportViewer } from "../model/report-viewer";
import { ReservationHisRequest } from "../model/reservation-his-request";
import { AppointmetReservated } from "../model/appointmet-reservated";

@Injectable()
export class ReservationService {
  constructor(private dataService: DataService) {}

  private readonly getAllPosibleRescheduleReservationEndPoint: string =
    "/reservation/getAllPosibleRescheduleReservation/";
  private readonly medicalAppointmentConfirmationUpdateEndPoint: string =
    "/reservation/medicalAppointmentConfirmationUpdate/";
  private readonly medicalAppointmentConfirmationRollBackEndPoint: string =
    "/reservation/medicalAppointmentConfirmationRollBack/";
  private readonly getMedicalAppointmentConfirmationsEndPoint: string =
    "/reservation/getMedicalAppointmentConfirmations/";
  private readonly getReservationQuestionsEndPoint: string =
    "/reservation/getReservationQuestions/";
  private readonly durationExtraordinaryReservationEndPoint: string =
    "/reservation/durationExtraordinaryReservation/";
  private readonly reservationRisCalendarEndPoint: string =
    "/reservation/reservationRisCalendar/";
  private readonly reservationCTCalendarEndPoint: string =
    "/reservation/reservationCTCalendar/";
  private readonly finalizeReservationEndPoint: string =
    "/reservation/finalizeReservation/";
  private readonly medicalAppointmentCancelledReservationEndPoint: string =
    "/reservation/medicalAppointmentCancelledReservation/";
  private readonly finalizeExtraordinaryReservationEndPoint: string =
    "/reservation/finalizeExtraordinaryReservation/";
  private readonly completePatientInfoEndPoint: string =
    "/reservation/completePatientInfo/";
  private readonly sendReservationMailEndPoint: string =
    "/reservation/sendReservationMail/";
  private readonly getMedicalAppointmentCancelReasonsEndPoint: string =
    "/reservation/getMedicalAppointmentCancelReasons/";
  private readonly cancelMedicalAppointmentEndPoint: string =
    "/reservation/cancelMedicalAppointment/";
  private readonly getReservationSearchItemsEndPoint: string =
    "/reservation/getReservationSearchItems/";
  private readonly getReservationReportEndPoint: string =
    "/reservation/getReservationReport/";
  private readonly getReservationSearchItemsForConfirmationEndPoint: string =
    "/reservation/getReservationSearchItemsForConfirmation/";
  private readonly getRescheduleReservationEndPoint: string =
    "/reservation/getRescheduleReservation/";
  private readonly getReminderEndPoint: string = "/reservation/getReminder/";
  private readonly getViewInfoEndPoint: string = "/reservation/getViewInfo/";
  private readonly getReservationSearchItemsForInconsistentEndPoint: string =
    "/reservation/getReservationSearchItemsForInconsistent/";
  private readonly inconsistentReservationToReserveEndPoint: string =
    "/reservation/inconsistentReservationToReserve/";
  private readonly changeReservationEquipmentPhysicianEndPoint: string =
    "/reservation/changeReservationEquipmentPhysician/";
  private readonly getReservationSearchItemsForRescheduleEndPoint: string =
    "/reservation/getReservationSearchItemsForReschedule/";
  private readonly getReservationEndPoint: string =
    "/reservation/getReservation/";
  private readonly getTransferTimeEndPoint: string =
    "/reservation/getTransferTime/";
  private readonly validateSlotReservationEndPoint: string =
    "/reservation/validateSlotReservation/";
  private readonly getCancelledReservationReasonFiltersEndPoint: string =
    "/reservation/getCancelledReservationReasonFilters/";
  private readonly getMedicalAppointmentWaitSearchItemsEndPoint: string =
    "/reservation/getMedicalAppointmentWaitSearchItems/";
  private readonly getMedicalAppointmentWaitReservationEndPoint: string =
    "/reservation/getMedicalAppointmentWaitReservation/";
  private readonly getMedicalAppointmentWaitTraceEndPoint: string =
    "/reservation/getMedicalAppointmentWaitTrace/";
  private readonly medicalAppointmentWaitStateUpdateEndPoint: string =
    "/reservation/medicalAppointmentWaitStateUpdate/";
  private readonly getMedicalAppointmentWaitStatesEndPoint: string =
    "/reservation/getMedicalAppointmentWaitStates/";
  private readonly getReservationHisRequestEndPoint: string =
    "/reservation/getReservationHisRequest/";
  private readonly createPendingReservationEndPoint: string =
    "/reservation/createPendingReservation/";
  private readonly getReservationEquipmentAvailabilityEndPoint: string =
    "/reservation/getReservationEquipmentAvailability/";


  async getAppointmetReservated(medicalAppointmentIds: number[]): Promise<AppointmetReservated[]> {
    return await this.dataService
      .post<AppointmetReservated[]>('/reservation/GetAppointmetReservated', medicalAppointmentIds)
      .toPromise();
  }

  async getReservationHisRequest(
    reservationHisRequestId: number
  ): Promise<ReservationHisRequest> {
    const params = { reservationHisRequestId: reservationHisRequestId };
    return await this.dataService
      .get<ReservationHisRequest>(this.getReservationHisRequestEndPoint, params)
      .toPromise();
  }

  async createPendingReservation(reservation: Reservation): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.createPendingReservationEndPoint, reservation)
      .toPromise();
  }

  async medicalAppointmentWaitStateUpdate(
    medicalAppointmentWaitId: number,
    medicalAppointmentWaitStateId: number,
    observation: string
  ): Promise<boolean> {
    const param = {
      MedicalAppointmentWaitId: medicalAppointmentWaitId,
      MedicalAppointmentWaitStateId: medicalAppointmentWaitStateId,
      Obervation: observation,
    };
    return await this.dataService
      .post<boolean>(this.medicalAppointmentWaitStateUpdateEndPoint, param)
      .toPromise();
  }

  async changeReservationEquipmentPhysician(
    equipmentPhysisicianChange: EquipmentPhysisicianChange
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.changeReservationEquipmentPhysicianEndPoint,
        equipmentPhysisicianChange
      )
      .toPromise();
  }

  async inconsistentReservationToReserve(
    medicalAppointmentIds: number[]
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.inconsistentReservationToReserveEndPoint,
        medicalAppointmentIds
      )
      .toPromise();
  }

  async medicalAppointmentCancelledReservation(
    cancelledReservationReasonId: number,
    observation: string,
    reservation: Reservation
  ): Promise<boolean> {
    const param = {
      CancelledReservationReasonId: cancelledReservationReasonId,
      Observation: observation,
      Reservation: reservation,
    };
    return await this.dataService
      .post<boolean>(this.medicalAppointmentCancelledReservationEndPoint, param)
      .toPromise();
  }

  async durationExtraordinaryReservation(
    serviceReservation: ServiceReservation,
    equipmentId: number,
    physicianId: number
  ): Promise<number> {
    const params = {
      ServiceReservation: serviceReservation,
      EquipmentId: equipmentId,
      PhysicianId: physicianId,
    };
    return await this.dataService
      .post<number>(this.durationExtraordinaryReservationEndPoint, params)
      .toPromise();
  }

  async MedicalAppointmentConfirmationUpdate(
    medicalAppointmetIds: number[],
    confirmationStatusId: number,
    observation: string
  ): Promise<boolean> {
    const params = {
      MedicalAppointmenteIds: medicalAppointmetIds,
      Observation: observation,
      ConfirmationStatusId: confirmationStatusId,
    };
    return await this.dataService
      .post<boolean>(this.medicalAppointmentConfirmationUpdateEndPoint, params)
      .toPromise();
  }

  async medicalAppointmentConfirmationRollBack(
    medicalAppointmetIds: number[]
  ): Promise<boolean> {
    const params = {
      MedicalAppointmenteIds: medicalAppointmetIds,
    };
    return await this.dataService
      .post<boolean>(
        this.medicalAppointmentConfirmationRollBackEndPoint,
        params
      )
      .toPromise();
  }

  async getReservation(medicalAppointmentId: number): Promise<ReservationInfo> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<ReservationInfo>(this.getReservationEndPoint, params)
      .toPromise();
  }

  async getReservationSearchItemsForReschedule(
    medicalAppointmentId: number,
    sameCenter: boolean
  ): Promise<ReservationSearchItem[]> {
    const params = {
      medicalAppointmentId: medicalAppointmentId,
      sameCenter: sameCenter,
    };
    return await this.dataService
      .get<ReservationSearchItem[]>(
        this.getReservationSearchItemsForRescheduleEndPoint,
        params
      )
      .toPromise();
  }

  async getReminder(medicalAppointmentId: number): Promise<Reminder> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<Reminder>(this.getReminderEndPoint, params)
      .toPromise();
  }

  async getViewInfo(medicalAppointmentId: number): Promise<LogViewInfo> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<LogViewInfo>(this.getViewInfoEndPoint, params)
      .toPromise();
  }

  async getMedicalAppointmentWaitTrace(
    medicalAppointmentWaitId: number
  ): Promise<MedicalAppointmentWaitTrace[]> {
    const params = { medicalAppointmentWaitId: medicalAppointmentWaitId };
    return await this.dataService
      .get<MedicalAppointmentWaitTrace[]>(
        this.getMedicalAppointmentWaitTraceEndPoint,
        params
      )
      .toPromise();
  }

  async getRescheduleReservation(
    medicalAppointmentIds: number[]
  ): Promise<ReservationInfo> {
    const params = {
      medicalAppointmentIds: JSON.stringify(medicalAppointmentIds),
    };
    return await this.dataService
      .get<ReservationInfo>(this.getRescheduleReservationEndPoint, params)
      .toPromise();
  }

  async getMedicalAppointmentWaitReservation(
    medicalAppointmentWaitIds: number[]
  ): Promise<ReservationInfo> {
    const params = {
      medicalAppointmentWaitIds: JSON.stringify(medicalAppointmentWaitIds),
    };
    return await this.dataService
      .get<ReservationInfo>(
        this.getMedicalAppointmentWaitReservationEndPoint,
        params
      )
      .toPromise();
  }

  async getAllPosibleRescheduleReservation(
    medicalAppointmentId: number
  ): Promise<ReservationInfo> {
    const params = {
      medicalAppointmentId: JSON.stringify(medicalAppointmentId),
    };
    return await this.dataService
      .get<ReservationInfo>(
        this.getAllPosibleRescheduleReservationEndPoint,
        params
      )
      .toPromise();
  }

  async getMedicalAppointmentWaitSearchItems(
    dateFrom: Date,
    dateTo: Date,
    practiceRegionId: number = null,
    medicalAppointmentWaitStateId: number = null,
    patientId: number = null,
    serviceId: number = null,
    centerId: number = null,
    onlyInterned: boolean = false
  ): Promise<MedicalAppointmentWaitSearchItem[]> {
    const params = {
      dateFrom: new Date(
        dateFrom.getFullYear(),
        dateFrom.getMonth(),
        dateFrom.getDate()
      ).toDateString(),
      dateTo: new Date(
        dateTo.getFullYear(),
        dateTo.getMonth(),
        dateTo.getDate()
      ).toDateString(),
      practiceRegionId: practiceRegionId,
      medicalAppointmentWaitStateId: medicalAppointmentWaitStateId,
      patientId: patientId,
      serviceId: serviceId,
      centerId,
      onlyInterned,
    };
    return await this.dataService
      .get<MedicalAppointmentWaitSearchItem[]>(
        this.getMedicalAppointmentWaitSearchItemsEndPoint,
        params
      )
      .toPromise();
  }

  async getReservationSearchItemsForInconsistent(
    from?: Date,
    to?: Date,
    patienteId?: number,
    equipmentId?: number,
    serviceId?: number,
    physicianId?: number,
    entityId?: number,
    planId?: number
  ): Promise<ReservationSearchItem[]> {
    const params = {
      from: from
        ? new Date(
            from.getFullYear(),
            from.getMonth(),
            from.getDate()
          ).toDateString()
        : null,
      to: to
        ? new Date(to.getFullYear(), to.getMonth(), to.getDate()).toDateString()
        : null,
      patienteId: patienteId,
      equipmentId: equipmentId,
      serviceId: serviceId,
      physicianId: physicianId,
      entityId: entityId,
      planId: planId,
    };

    return await this.dataService
      .get<ReservationSearchItem[]>(
        this.getReservationSearchItemsForInconsistentEndPoint,
        params
      )
      .toPromise();
  }

  async getReservationSearchItemsForConfirmation(
    viewCancel: boolean,
    from: Date,
    to: Date,
    serviceIds: number[],
    centerIds: number[],
    patienteId?: number,
    confirmationStatusId?: number
  ): Promise<ReservationSearchItem[]> {
    const params = {
      viewCancel: viewCancel,
      serviceIds: JSON.stringify(serviceIds),
      centerIds: JSON.stringify(centerIds),
      from: new Date(
        from.getFullYear(),
        from.getMonth(),
        from.getDate()
      ).toDateString(),
      to: new Date(
        to.getFullYear(),
        to.getMonth(),
        to.getDate()
      ).toDateString(),
      patienteId: patienteId,
      confirmationStatusId: confirmationStatusId,
    };

    return await this.dataService
      .get<ReservationSearchItem[]>(
        this.getReservationSearchItemsForConfirmationEndPoint,
        params
      )
      .toPromise();
  }

  async getReservationReport(
    over: boolean,
    cancel: boolean,
    atended: boolean,
    from?: Date,
    to?: Date,
    patienteId?: number,
    equipmentId?: number,
    serviceId?: number,
    physicianId?: number,
    practiceRegionId?: number,
    assignmentFrom?: Date,
    assignmentTo?: Date,
    centerId?: number
  ): Promise<ReportViewer> {
    const params = {
      over: over,
      cancel: cancel,
      atended: atended,
      from: from
        ? new Date(
            from.getFullYear(),
            from.getMonth(),
            from.getDate()
          ).toDateString()
        : null,
      to: to
        ? new Date(to.getFullYear(), to.getMonth(), to.getDate()).toDateString()
        : null,
      patienteId: patienteId,
      equipmentId: equipmentId,
      serviceId: serviceId,
      physicianId: physicianId,
      practiceRegionId: practiceRegionId,
      assignmentFrom: assignmentFrom,
      assignmentTo: assignmentTo,
      centerId: centerId,
    };

    return await this.dataService
      .get<ReportViewer>(this.getReservationReportEndPoint, params)
      .toPromise();
  }

  async getReminderReport(
    medicalAppointmentIds: number[]
  ): Promise<ReportViewer> {
    const params = { MedicalAppointmentIds: medicalAppointmentIds };
    return await this.dataService
      .post<ReportViewer>("/reservation/getReminderReport", params)
      .toPromise();
  }

  async getReminderHtml(medicalAppointmentIds: number[]): Promise<string> {
    const params = { MedicalAppointmentIds: medicalAppointmentIds };
    return await this.dataService
      .post<string>("/reservation/getReminderReportHtml", params)
      .toPromise();
  }

  async getAppointmentsByAutomaticAdmission(
    orientationId: number,
    patientId: Number
  ): Promise<number[]> {
    const params = { orientationId, patientId  };
    return await this.dataService
      .get<number[]>("/reservation/getAppointmentsByAutomaticAdmission", params)
      .toPromise();
  }

  async getReservationSearchItems(
    over: boolean,
    cancel: boolean,
    atended: boolean,
    orderByPatient: boolean,
    from?: Date,
    to?: Date,
    patienteId?: number,
    equipmentId?: number,
    serviceId?: number,
    physicianId?: number,
    practiceRegionId?: number,
    assignmentFrom?: Date,
    assignmentTo?: Date,
    centerId?: number
  ): Promise<ReservationSearchItem[]> {
    const params = {
      over: over,
      cancel: cancel,
      atended: atended,
      from: from
        ? new Date(
            from.getFullYear(),
            from.getMonth(),
            from.getDate()
          ).toDateString()
        : null,
      to: to
        ? new Date(to.getFullYear(), to.getMonth(), to.getDate()).toDateString()
        : null,
      patienteId: patienteId,
      equipmentId: equipmentId,
      serviceId: serviceId,
      physicianId: physicianId,
      practiceRegionId: practiceRegionId,
      assignmentFrom: assignmentFrom,
      assignmentTo: assignmentTo,
      centerId: centerId,
      orderByPatient,
    };

    return await this.dataService
      .get<ReservationSearchItem[]>(
        this.getReservationSearchItemsEndPoint,
        params
      )
      .toPromise();
  }

  async getMedicalAppointmentCancelReasons(): Promise<
    MedicalAppointmentCancelReason[]
  > {
    const params = {};
    return await this.dataService
      .get<MedicalAppointmentCancelReason[]>(
        this.getMedicalAppointmentCancelReasonsEndPoint,
        params
      )
      .toPromise();
  }

  async getReservationQuestions(
    practiceRegionIds: number[],
    patientId?: number,
    medicalAppointmentIds?: number[]
  ): Promise<ReservationQuestionInfo> {
    const params = {
      practiceRegionIds: JSON.stringify(practiceRegionIds),
      patientId: patientId,
      medicalAppointmentIds: medicalAppointmentIds
        ? JSON.stringify(medicalAppointmentIds)
        : "",
    };
    return await this.dataService
      .get<ReservationQuestionInfo>(
        this.getReservationQuestionsEndPoint,
        params
      )
      .toPromise();
  }

  async getMedicalAppointmentWaitStates(): Promise<
    MedicalAppointmentWaitState[]
  > {
    const params = {};
    return await this.dataService
      .get<MedicalAppointmentWaitState[]>(
        this.getMedicalAppointmentWaitStatesEndPoint,
        params
      )
      .toPromise();
  }

  async getReservationEquipmentAvailability(
    reservation: Reservation
  ): Promise<ReservationEquipmentAvailability> {
    return await this.dataService
      .post<ReservationEquipmentAvailability>(
        this.getReservationEquipmentAvailabilityEndPoint,
        reservation
      )
      .toPromise();
  }

  async reservationRisCalendar(
    reservation: Reservation
  ): Promise<ReservationCalendar> {
    return await this.dataService
      .post<ReservationCalendar>(
        this.reservationRisCalendarEndPoint,
        reservation
      )
      .toPromise();
  }

  async reservationCTCalendar(
    reservation: Reservation
  ): Promise<ReservationCalendar> {
    return await this.dataService
      .post<ReservationCalendar>(
        this.reservationCTCalendarEndPoint,
        reservation
      )
      .toPromise();
  }

  async finalizeReservation(reservation: Reservation): Promise<Reminder[]> {
    return await this.dataService
      .post<Reminder[]>(this.finalizeReservationEndPoint, reservation)
      .toPromise();
  }

  async validateSlotReservation(
    reservation: Reservation
  ): Promise<SlotValidationResponse> {
    return await this.dataService
      .post<SlotValidationResponse>(
        this.validateSlotReservationEndPoint,
        reservation
      )
      .toPromise();
  }

  async finalizeExtraordinaryReservation(
    reservation: Reservation,
    reservationExtraordinaryCalendar: ReservationExtraordinaryCalendar
  ): Promise<number> {
    const param = {
      Reservation: reservation,
      ReservationExtraordinaryCalendar: reservationExtraordinaryCalendar,
    };
    return await this.dataService
      .post<number>(this.finalizeExtraordinaryReservationEndPoint, param)
      .toPromise();
  }

  async completePatientInfo(
    reservationSlots: ReservationSlot[]
  ): Promise<ReservationSlot[]> {
    return await this.dataService
      .post<ReservationSlot[]>(
        this.completePatientInfoEndPoint,
        reservationSlots
      )
      .toPromise();
  }

  async cancelMedicalAppointment(
    medicalAppointmentCancel: MedicalAppointmentCancel
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.cancelMedicalAppointmentEndPoint,
        medicalAppointmentCancel
      )
      .toPromise();
  }

  async sendResevationMail(medicalAppointmentIds: number[]): Promise<void> {
    const params = {
      medicalAppointmentIds: JSON.stringify(medicalAppointmentIds),
    };
    return await this.dataService
      .get<void>(this.sendReservationMailEndPoint, params)
      .toPromise();
  }

  async getMedicalAppointmentConfirmations(
    medicalAppointmetId: number
  ): Promise<MedicalAppointmentConfirmation[]> {
    const params = { medicalAppointmetId: medicalAppointmetId };
    return await this.dataService
      .get<MedicalAppointmentConfirmation[]>(
        this.getMedicalAppointmentConfirmationsEndPoint,
        params
      )
      .toPromise();
  }

  async getTransferTime(): Promise<any> {
    return await this.dataService
      .get<any>(this.getTransferTimeEndPoint, {})
      .toPromise();
  }

  async getCancelledReservationReasonFilters(): Promise<
    CancelledReservationReasonFilter[]
  > {
    return await this.dataService
      .get<CancelledReservationReasonFilter[]>(
        this.getCancelledReservationReasonFiltersEndPoint,
        {}
      )
      .toPromise();
  }
}
